import { Button, Group, NumberInput, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { i18nKeys, useTranslation } from 'locales';
import type { CreateTaskTypeVariables, TaskType } from 'modules/Tasks/api';

type TaskTypeFormValues = {
  name: string;
  defaultTitle: string;
  defaultDescription: string;
  defaultDueOffsetDays: number | '';
};

type TaskFormProps = {
  taskType?: TaskType;
  onSubmit: (values: CreateTaskTypeVariables) => void;
  onCancel?: () => void;
};

const toDefaults = (taskType: TaskType | undefined): TaskTypeFormValues => ({
  name: taskType?.name ?? '',
  defaultTitle: taskType?.defaultTitle ?? '',
  defaultDescription: taskType?.defaultDescription ?? '',
  defaultDueOffsetDays: taskType?.defaultDueOffsetDays ?? '',
});

const toPayload = (values: TaskTypeFormValues): CreateTaskTypeVariables => ({
  name: values.name,
  defaultTitle: values.defaultTitle.trim() || null,
  defaultDescription: values.defaultDescription.trim() || null,
  defaultDueOffsetDays:
    values.defaultDueOffsetDays === '' ? null : Number(values.defaultDueOffsetDays),
});

export const TaskTypeForm = ({ taskType, onSubmit, onCancel }: TaskFormProps) => {
  const { t } = useTranslation();

  const form = useForm<TaskTypeFormValues>({
    initialValues: toDefaults(taskType),
  });

  return (
    <form onSubmit={form.onSubmit(values => onSubmit(toPayload(values)))}>
      <Stack p="md" gap="sm">
        <TextInput
          label={t(i18nKeys.TASK.ATTRIBUTES.TITLE)}
          placeholder={t(i18nKeys.SETTINGS.TASKS.NAME_PLACEHOLDER)}
          required
          autoFocus
          {...form.getInputProps('name')}
        />
        <TextInput
          label={t(i18nKeys.SETTINGS.TASKS.DEFAULT_TITLE)}
          placeholder={t(i18nKeys.SETTINGS.TASKS.DEFAULT_TITLE_PLACEHOLDER)}
          {...form.getInputProps('defaultTitle')}
        />
        <Textarea
          label={t(i18nKeys.SETTINGS.TASKS.DEFAULT_DESCRIPTION)}
          placeholder={t(i18nKeys.SETTINGS.TASKS.DEFAULT_DESCRIPTION_PLACEHOLDER)}
          autosize
          minRows={3}
          {...form.getInputProps('defaultDescription')}
        />
        <NumberInput
          label={t(i18nKeys.SETTINGS.TASKS.DEFAULT_DUE_OFFSET_DAYS)}
          min={0}
          allowNegative={false}
          allowDecimal={false}
          {...form.getInputProps('defaultDueOffsetDays')}
        />
        <Text size="xs" c="dimmed">
          {t(i18nKeys.SETTINGS.TASKS.TEMPLATE_HINT)}
        </Text>
        <Group justify="flex-end" gap="xs" mt="xs">
          <Button variant="default" onClick={onCancel}>
            {t(i18nKeys.CANCEL)}
          </Button>
          <Button type="submit">{t(i18nKeys.SAVE)}</Button>
        </Group>
      </Stack>
    </form>
  );
};
